import { VisualShape, VisualState, TranscriptItem, GroundingMetadata } from 'types'
import {
  type CanvasSurface,
  hasRenderableWeatherData,
  sanitizeIntentForChat,
} from '../lib/chat-canvas-shapes.js'
import { agentToShape, getStageShape } from '../lib/canvas-shape-taxonomy.js'
import { contextDetector } from './contextDetection.js'

export { agentToShape, stageToShape } from '../lib/canvas-shape-taxonomy.js'

export function resolveAgentShape(agent?: string | null, stage?: string | null): VisualShape {
  if (agent) {
    if (agentToShape[agent]) return agentToShape[agent]
    const lower = agent.toLowerCase()
    if (lower.includes('discovery')) return 'discovery'
    if (lower.includes('score')) return 'scoring'
    if (lower.includes('workshop')) return 'workshop'
    if (lower.includes('consult')) return 'consulting'
    if (lower.includes('close')) return 'closer'
    if (lower.includes('summary')) return 'summary'
    if (lower.includes('proposal')) return 'proposal'
    if (lower.includes('admin')) return 'admin'
    if (lower.includes('retarget')) return 'retargeting'
  }
  if (stage) return getStageShape(stage)
  return 'orb'
}

// Helper for strict word matching
const hasWord = (text: string, word: string) => new RegExp(`\\b${word}\\b`, 'i').test(text)
const hasPhrase = (text: string, phrase: string) => text.toLowerCase().includes(phrase)

export interface IntentContext {
  groundingMetadata?: GroundingMetadata
  location?: { latitude: number; longitude: number; city?: string }
  transcript?: TranscriptItem[]
  source?: 'screenshare' | 'screen' | 'webcam'
  streamType?: string
  /** When `chat`, landing demo shapes (globe, brain, solarSystem, …) are gated. */
  surface?: CanvasSurface
}

export interface IntentResult {
  shape: VisualShape | null
  textContent?: string
  weatherData?: VisualState['weatherData']
  chartData?: VisualState['chartData']
  mapData?: VisualState['mapData']
  // New context-driven properties
  solarSystemData?: {
    focusPlanet?: string
    showOrbits?: boolean
    scale?: 'realistic' | 'educational'
  }
  stockData?: {
    symbol?: string
    timeframe?: '1D' | '1W' | '1M' | '3M' | '1Y'
    showVolume?: boolean
  }
  networkData?: {
    nodes: string[]
    connections: Array<{ from: string; to: string; strength: number }>
    layout: 'force' | 'circular' | 'hierarchical'
  }
  moleculeData?: {
    formula?: string
    structure?: 'ball-stick' | 'space-filling' | 'wireframe'
    animate?: boolean
  }
  mathData?: {
    equation?: string
    graph3d?: boolean
    domain?: [number, number]
    range?: [number, number]
  }
}

const clampTemperature = (value: number): number => {
  if (!Number.isFinite(value)) return value
  return Math.max(-60, Math.min(60, value))
}

const parseTemperatureCandidate = (
  rawValue: string,
  rawUnit?: string
): { display: string; value: number } | undefined => {
  const numeric = Number.parseInt(rawValue, 10)
  if (!Number.isFinite(numeric)) return undefined

  const unit = rawUnit?.toLowerCase()
  const celsius = unit?.startsWith('f') ? Math.round(((numeric - 32) * 5) / 9) : numeric

  const normalized = clampTemperature(celsius)

  return {
    display: `${normalized}\u00B0C`,
    value: normalized,
  }
}

const extractNormalizedTemperature = (
  text: string
): { display: string; value: number } | undefined => {
  const explicitMatches = Array.from(
    text.matchAll(/(-?\d+)\s*(?:°|degrees?|deg)?\s*(c|f|celsius|fahrenheit)\b/gi)
  )

  const explicitCelsius = explicitMatches.find(match => match[2]?.toLowerCase().startsWith('c'))
  if (explicitCelsius?.[1]) {
    return parseTemperatureCandidate(explicitCelsius[1], explicitCelsius[2])
  }

  const explicitFahrenheit = explicitMatches.find(match => match[2]?.toLowerCase().startsWith('f'))
  if (explicitFahrenheit?.[1]) {
    return parseTemperatureCandidate(explicitFahrenheit[1], explicitFahrenheit[2])
  }

  const genericMatch = text.match(/(-?\d+)\s*(?:°|degrees?|deg)\b(?!\s*[NSEW])/i)
  if (genericMatch?.[1]) {
    return parseTemperatureCandidate(genericMatch[1], 'c')
  }

  return undefined
}

export const extractWeatherData = (text: string): VisualState['weatherData'] | undefined => {
  const t = text.toLowerCase()

  const normalizedTemperature = extractNormalizedTemperature(text)

  let condition: 'sunny' | 'cloudy' | 'rainy' | 'snowy' | 'stormy' = 'cloudy'

  if (
    hasWord(t, 'snow') ||
    hasWord(t, 'flurries') ||
    hasWord(t, 'blizzard') ||
    hasWord(t, 'freezing')
  )
    condition = 'snowy'
  else if (
    hasWord(t, 'rain') ||
    hasWord(t, 'rainy') ||
    hasWord(t, 'drizzle') ||
    hasWord(t, 'shower') ||
    hasWord(t, 'wet')
  )
    condition = 'rainy'
  else if (
    hasWord(t, 'storm') ||
    hasWord(t, 'stormy') ||
    hasWord(t, 'thunder') ||
    hasWord(t, 'lightning')
  )
    condition = 'stormy'
  else if (hasWord(t, 'sun') || hasWord(t, 'sunny') || hasWord(t, 'clear') || hasWord(t, 'bright'))
    condition = 'sunny'

  const hasContext =
    hasWord(t, 'weather') ||
    hasWord(t, 'temperature') ||
    hasWord(t, 'forecast') ||
    hasWord(t, 'climate') ||
    hasWord(t, 'celsius') ||
    hasWord(t, 'fahrenheit') ||
    hasWord(t, 'degrees') ||
    !!normalizedTemperature

  if (hasContext && (condition !== 'cloudy' || normalizedTemperature)) {
    return {
      condition,
      ...(normalizedTemperature
        ? {
            temperature: normalizedTemperature.display,
            temperatureValue: normalizedTemperature.value,
          }
        : {}),
    }
  }
  return undefined
}

export const extractChartData = (text: string): VisualState['chartData'] | undefined => {
  const t = text.toLowerCase()
  let trend: 'up' | 'down' | 'neutral' = 'neutral'

  if (
    hasWord(t, 'up') ||
    hasWord(t, 'rose') ||
    hasWord(t, 'increase') ||
    hasWord(t, 'gain') ||
    hasWord(t, 'bull') ||
    hasWord(t, 'high') ||
    /\+\d/.test(text)
  )
    trend = 'up'
  else if (
    hasWord(t, 'down') ||
    hasWord(t, 'fell') ||
    hasWord(t, 'drop') ||
    hasWord(t, 'loss') ||
    hasWord(t, 'bear') ||
    hasWord(t, 'low') ||
    /-\d/.test(text)
  )
    trend = 'down'

  const valueRegex = /(\$\d[\d,.]*|\d[\d,.]*%)/
  const match = text.match(valueRegex)
  const value = match ? match[0] : undefined

  const numericValue = (() => {
    if (!value) return undefined
    if (value.includes('%')) {
      const parsedPercent = Number.parseFloat(value.replace(/[%,$]/g, ''))
      return Number.isFinite(parsedPercent) ? parsedPercent : undefined
    }

    const parsedCurrency = Number.parseFloat(value.replace(/[$,]/g, ''))
    if (!Number.isFinite(parsedCurrency) || parsedCurrency <= 0) return undefined
    return Math.max(5, Math.min(95, Math.round(Math.log10(parsedCurrency + 1) * 28)))
  })()

  const label =
    value && value.startsWith('$')
      ? 'Market signal'
      : value && value.includes('%')
        ? 'Performance'
        : 'Live signal'

  if (trend !== 'neutral' || value) {
    return {
      trend,
      label,
      ...(value ? { value } : {}),
      ...(numericValue !== undefined ? { numericValue } : {}),
    }
  }
  return undefined
}

export const extractMapCoords = (uri: string): { lat: number; lng: number } | undefined => {
  const atMatch = uri.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/)
  if (atMatch && atMatch[1] && atMatch[2])
    return { lat: parseFloat(atMatch[1]), lng: parseFloat(atMatch[2]) }

  const llMatch = uri.match(/[?&]ll=(-?\d+\.\d+),(-?\d+\.\d+)/)
  if (llMatch && llMatch[1] && llMatch[2])
    return { lat: parseFloat(llMatch[1]), lng: parseFloat(llMatch[2]) }

  return undefined
}

function isWeatherQueryText(text: string): boolean {
  const t = text.toLowerCase()
  return (
    hasWord(t, 'weather') ||
    hasWord(t, 'forecast') ||
    hasWord(t, 'temperature') ||
    hasWord(t, 'celsius') ||
    hasWord(t, 'fahrenheit')
  )
}

function resolveWeatherIntent(text: string, context?: IntentContext): IntentResult | null {
  if (!isWeatherQueryText(text)) return null

  const data = extractWeatherData(text)
  const locationName =
    context?.location?.city || context?.location?.latitude ? 'Nearby' : undefined

  if (data && (context?.surface !== 'chat' || hasRenderableWeatherData(data))) {
    return {
      shape: 'weather',
      weatherData: {
        ...data,
        ...(locationName ? { location: locationName } : {}),
      },
    }
  }

  const t = text.toLowerCase()
  if (
    hasWord(t, 'weather') ||
    hasWord(t, 'temperature') ||
    hasWord(t, 'forecast') ||
    hasWord(t, 'cloudy') ||
    hasWord(t, 'sunny') ||
    hasWord(t, 'rainy') ||
    hasWord(t, 'snowy') ||
    hasWord(t, 'stormy')
  ) {
    const normalizedTemperature = extractNormalizedTemperature(text)
    let condition: 'sunny' | 'cloudy' | 'rainy' | 'snowy' | 'stormy' = 'cloudy'
    if (hasWord(t, 'snow') || hasWord(t, 'flurries')) condition = 'snowy'
    else if (hasWord(t, 'rain') || hasWord(t, 'rainy') || hasWord(t, 'drizzle')) condition = 'rainy'
    else if (hasWord(t, 'storm') || hasWord(t, 'thunder')) condition = 'stormy'
    else if (hasWord(t, 'sun') || hasWord(t, 'sunny') || hasWord(t, 'clear')) condition = 'sunny'

    const weatherData = {
      condition,
      ...(normalizedTemperature
        ? {
            temperature: normalizedTemperature.display,
            temperatureValue: normalizedTemperature.value,
          }
        : {}),
    }

    if (context?.surface !== 'chat' || hasRenderableWeatherData(weatherData)) {
      return { shape: 'weather', weatherData }
    }
  }

  return null
}

function finalizeIntentResult(
  result: IntentResult | null,
  context?: IntentContext,
): IntentResult | null {
  if (!result?.shape) return null
  if (context?.surface !== 'chat') return result
  return sanitizeIntentForChat(result) as IntentResult | null
}

export const detectVisualIntent = (text: string, context?: IntentContext): IntentResult | null =>
  finalizeIntentResult(runDetectVisualIntent(text, context), context)

function runDetectVisualIntent(text: string, context?: IntentContext): IntentResult | null {
  const normalizedSource = context?.source?.toLowerCase()
  if (normalizedSource === 'screenshare' || context?.streamType === 'screen_share_stream') {
    return { shape: 'screenshare' }
  }

  const weatherFirst = resolveWeatherIntent(text, context)
  if (weatherFirst) return weatherFirst

  // 0. Grounding-First detection
  if (context?.groundingMetadata?.groundingChunks) {
    const chunks = context.groundingMetadata.groundingChunks

    if (isWeatherQueryText(text)) {
      const groundedWeather = resolveWeatherIntent(text, context)
      if (groundedWeather) return groundedWeather
    }

    // Priority 1: Map grounding (skip for chat weather queries)
    const mapChunk = chunks.find(
      (c: any) =>
        c.web?.uri?.includes('google.com/maps') ||
        c.web?.title?.toLowerCase().includes('location') ||
        c.maps ||
        c.retrievedUri?.includes('maps')
    )

    if (mapChunk && !(context?.surface === 'chat' && isWeatherQueryText(text))) {
      const uri = mapChunk.web?.uri || (mapChunk as any).retrievedUri || ''
      const coords = extractMapCoords(uri)
      return {
        shape: 'map',
        mapData: {
          title: mapChunk.web?.title || 'Location',
          ...(coords ? { lat: coords.lat, lng: coords.lng } : {}),
        },
      }
    }
  }

  // Landing demo context detection — disabled on /chat (random network/solarSystem false positives)
  if (context?.surface !== 'chat') {
    const contextResult = contextDetector.detectContext(text)
    if (contextResult) {
      return contextResult
    }
  }

  const t = text.toLowerCase()

  // identity/text — intentionally keyword-based ("who are you"), no API signal needed
  if (
    hasPhrase(t, 'what is your name') ||
    hasPhrase(t, 'who are you') ||
    hasPhrase(t, 'your identity') ||
    hasPhrase(t, 'f.b/c') ||
    hasPhrase(t, 'antigravity')
  ) {
    return { shape: 'text', textContent: 'F.B/c' }
  }

  // scanner — keyword fallback (API: tool-based detection in Groups 1-4 section below)
  if (
    hasWord(t, 'analyze') ||
    hasWord(t, 'scan') ||
    hasWord(t, 'read') ||
    hasPhrase(t, 'check this') ||
    hasWord(t, 'review') ||
    hasPhrase(t, 'image displays') ||
    hasPhrase(t, 'file contains') ||
    hasPhrase(t, 'report details') ||
    hasPhrase(t, 'summary of') ||
    hasWord(t, 'report') ||
    hasWord(t, 'summary') ||
    (hasWord(t, 'analyzed') &&
      (hasWord(t, 'image') ||
        hasWord(t, 'file') ||
        hasWord(t, 'document') ||
        hasWord(t, 'content')))
  ) {
    return { shape: 'scanner' }
  }

  const hasScreenShareContext =
    hasPhrase(t, 'screen share') ||
    hasPhrase(t, 'screen-share') ||
    hasPhrase(t, 'screen_share') ||
    hasPhrase(t, 'screen shows') ||
    hasPhrase(t, 'shared screen') ||
    hasPhrase(t, 'on your screen') ||
    hasPhrase(t, 'in the browser') ||
    hasPhrase(t, 'in the tab') ||
    hasPhrase(t, 'ui shows') ||
    hasPhrase(t, 'dashboard shows')

  // code — keyword fallback (API: tool-based detection in Groups 1-4 section below)
  if (
    !hasScreenShareContext &&
    (hasWord(t, 'function') ||
      hasWord(t, 'const') ||
      hasWord(t, 'import') ||
      hasWord(t, 'class') ||
      hasWord(t, 'python') ||
      hasWord(t, 'javascript') ||
      hasPhrase(t, 'code block') ||
      hasWord(t, 'algorithm') ||
      hasWord(t, 'api') ||
      text.includes('```'))
  ) {
    return { shape: 'code' }
  }

  if (hasScreenShareContext) {
    return { shape: 'screenshare' }
  }

  if (
    hasPhrase(t, 'where is') ||
    hasWord(t, 'location') ||
    hasWord(t, 'map') ||
    hasWord(t, 'direction') ||
    hasWord(t, 'navigate') ||
    hasWord(t, 'gps') ||
    hasWord(t, 'route')
  ) {
    if (context?.surface === 'chat' && isWeatherQueryText(text)) {
      return null
    }
    return { shape: 'map' }
  }

  if (
    hasWord(t, 'stock') ||
    hasWord(t, 'price') ||
    hasWord(t, 'market') ||
    hasWord(t, 'chart') ||
    hasWord(t, 'graph') ||
    hasWord(t, 'bitcoin') ||
    hasWord(t, 'crypto') ||
    hasWord(t, 'currency') ||
    hasWord(t, 'trading') ||
    hasWord(t, 'data') ||
    hasWord(t, 'trend') ||
    hasWord(t, 'finance')
  ) {
    const data = extractChartData(text)
    if (data) {
      return { shape: 'chart', chartData: data }
    }
    return { shape: 'chart' }
  }

  if (
    hasWord(t, 'planet') ||
    hasWord(t, 'space') ||
    hasWord(t, 'orbit') ||
    hasWord(t, 'cosmos') ||
    hasWord(t, 'solar') ||
    hasWord(t, 'galaxy') ||
    hasWord(t, 'universe') ||
    hasWord(t, 'moon')
  ) {
    return { shape: 'planet' }
  }

  if (hasWord(t, 'time') || hasWord(t, 'clock') || hasPhrase(t, 'what hour')) {
    return { shape: 'clock' }
  }

  // =========================================================================
  // --- API-signal shape detection (Groups 1-4) ---
  // These use real API data (tools[], reasoning, groundingChunks, contextSources)
  // and take priority over keyword fallbacks below.
  // =========================================================================

  const lastItem = context?.transcript?.[context.transcript.length - 1]
  const tools = lastItem?.tools ?? []
  const activeToolNames = tools.map((t: any) => t.name)
  const hasReasoning = lastItem?.reasoning && lastItem.reasoning.length > 100
  const hasDeepReasoning = lastItem?.reasoning && lastItem.reasoning.length > 500
  const chunks = context?.groundingMetadata?.groundingChunks ?? []
  const queries = context?.groundingMetadata?.webSearchQueries ?? []
  const chunkUris = chunks.map((c: any) => c.web?.uri || '').filter(Boolean)
  const sources = (lastItem as any)?.contextSources ?? []
  const sourceTypes = sources.map((s: any) => s.type)

  // Group 1: Tool-based detection
  if (activeToolNames.some((n: string) => /get_weather|weather_lookup/i.test(n))) {
    const toolWeather = extractWeatherData(text)
    if (toolWeather && hasRenderableWeatherData(toolWeather)) {
      return { shape: 'weather', weatherData: toolWeather }
    }
    return null
  }
  // code: code_execution, file_read, file_analysis, python tools
  if (
    activeToolNames.some((n: string) => /code_execution|file_read|file_analysis|python/i.test(n))
  ) {
    return { shape: 'code' }
  }
  // scanner: analyze, scan, read_file, image_analysis tools
  if (activeToolNames.some((n: string) => /analyze|scan|read_file|image_analysis/i.test(n))) {
    return { shape: 'scanner' }
  }

  // Group 2: Reasoning-based detection
  // constellation: deep reasoning (>500 chars) — check before brain since it's more specific
  if (hasDeepReasoning) {
    return { shape: 'constellation' }
  }
  // brain: reasoning present (>100 chars) — landing only
  if (hasReasoning && context?.surface !== 'chat') {
    return { shape: 'brain' }
  }

  // Group 3: Grounding URI-based detection
  // chart: finance-related grounding URIs
  if (
    chunkUris.some((uri: string) =>
      /finance|stocks|market|tradingview|yahoo\.com\/finance/i.test(uri)
    )
  ) {
    const data = extractChartData(text)
    return { shape: 'chart', ...(data ? { chartData: data } : {}) }
  }
  // dna: medical/bio grounding URIs — landing only
  if (
    context?.surface !== 'chat' &&
    chunkUris.some((uri: string) => /pubmed|ncbi|nature\.com|medical|health/i.test(uri))
  ) {
    return { shape: 'dna' }
  }
  // atom: physics/science grounding URIs — landing only
  if (
    context?.surface !== 'chat' &&
    chunkUris.some((uri: string) => /physics|arxiv\.org|science|quantum/i.test(uri))
  ) {
    return { shape: 'atom' }
  }
  // shield: security grounding URIs
  if (chunkUris.some((uri: string) => /security|privacy|encryption|cybersec/i.test(uri))) {
    return { shape: 'shield' }
  }

  // Group 4: contextSources-based detection
  // scanner: file or webcam context source
  if (sourceTypes.includes('file') || sourceTypes.includes('webcam')) {
    return { shape: 'scanner' }
  }
  // globe: web context source — landing only
  if (context?.surface !== 'chat' && sourceTypes.includes('web')) {
    return { shape: 'globe' }
  }
  // map: location context source — prefer weather on chat when response includes temp
  if (sourceTypes.includes('location')) {
    if (context?.surface === 'chat') {
      const locationWeather = resolveWeatherIntent(text, context)
      if (locationWeather) return locationWeather
      return null
    }
    return { shape: 'map' }
  }

  // --- Grounding-enhanced shape detection ---
  if (queries.length > 0 && context?.surface !== 'chat') {
    // Real web search was performed — show globe shape
    return { shape: 'globe' }
  }

  // =========================================================================
  // --- Keyword fallbacks (safety net) ---
  // These fire only when no API signal matched above.
  // =========================================================================

  // globe — keyword fallback (API: webSearchQueries checked above) — landing only
  if (
    context?.surface !== 'chat' &&
    (hasWord(t, 'search') ||
      hasWord(t, 'google') ||
      hasWord(t, 'web') ||
      hasWord(t, 'internet') ||
      hasWord(t, 'online') ||
      hasWord(t, 'find') ||
      hasWord(t, 'browser'))
  ) {
    return { shape: 'globe' }
  }
  // brain — keyword fallback (API: reasoning length checked above) — landing only
  if (
    context?.surface !== 'chat' &&
    (hasWord(t, 'reason') ||
      hasWord(t, 'think') ||
      hasWord(t, 'logic') ||
      hasWord(t, 'plan') ||
      hasWord(t, 'solve') ||
      hasWord(t, 'compare') ||
      hasWord(t, 'study') ||
      hasWord(t, 'neural') ||
      hasWord(t, 'ai') ||
      hasWord(t, 'brainstorm'))
  ) {
    return { shape: 'brain' }
  }
  // heart — keyword-only (emotional/abstract concept, no API signal)
  if (
    hasWord(t, 'love') ||
    hasWord(t, 'heart') ||
    hasWord(t, 'feel') ||
    hasWord(t, 'care') ||
    hasWord(t, 'happy') ||
    hasWord(t, 'sad')
  ) {
    return { shape: 'heart' }
  }
  // grid — keyword-only (geometric/decorative, no API signal)
  if (
    hasWord(t, 'matrix') ||
    hasWord(t, 'grid') ||
    hasWord(t, 'system') ||
    hasWord(t, 'computer') ||
    hasWord(t, 'digital')
  ) {
    return { shape: 'grid' }
  }
  // dna — keyword fallback (API: grounding URI checked above)
  if (
    hasWord(t, 'dna') ||
    hasWord(t, 'life') ||
    hasWord(t, 'bio') ||
    hasWord(t, 'gene') ||
    hasWord(t, 'cell') ||
    hasWord(t, 'health')
  ) {
    return { shape: 'dna' }
  }
  // atom — keyword fallback (API: grounding URI checked above)
  if (
    hasWord(t, 'atom') ||
    hasWord(t, 'physics') ||
    hasWord(t, 'energy') ||
    hasWord(t, 'nuclear') ||
    hasWord(t, 'science') ||
    hasWord(t, 'quantum')
  ) {
    return { shape: 'atom' }
  }
  // hourglass — keyword-only (abstract concept, no API signal)
  if (
    hasWord(t, 'history') ||
    hasWord(t, 'wait') ||
    hasWord(t, 'ancient') ||
    hasWord(t, 'future')
  ) {
    return { shape: 'hourglass' }
  }

  // constellation — keyword fallback (API: deep reasoning checked above)
  if (
    hasWord(t, 'why') ||
    hasWord(t, 'mystery') ||
    hasWord(t, 'philosophy') ||
    hasWord(t, 'confuse') ||
    hasWord(t, 'connect')
  ) {
    return { shape: 'constellation' }
  }

  // shield — keyword fallback (API: grounding URI checked above)
  if (
    hasWord(t, 'secure') ||
    hasWord(t, 'safe') ||
    hasWord(t, 'protect') ||
    hasWord(t, 'shield') ||
    hasWord(t, 'private') ||
    hasWord(t, 'lock')
  ) {
    return { shape: 'shield' }
  }
  // star — keyword-only (abstract concept, no API signal)
  if (
    hasWord(t, 'idea') ||
    hasWord(t, 'star') ||
    hasWord(t, 'magic') ||
    hasWord(t, 'success') ||
    hasWord(t, 'win') ||
    hasWord(t, 'spark')
  ) {
    return { shape: 'star' }
  }

  // Geometric shapes — keyword-only (geometric/decorative, no API signal)
  if (
    hasWord(t, 'rectangle') ||
    hasWord(t, 'rect') ||
    hasWord(t, 'box') ||
    hasWord(t, 'square') ||
    hasWord(t, 'frame') ||
    hasWord(t, 'shape')
  ) {
    return { shape: 'rect' }
  }
  if (
    hasWord(t, 'wave') ||
    hasWord(t, 'ocean') ||
    hasWord(t, 'water') ||
    hasWord(t, 'flow') ||
    hasWord(t, 'current') ||
    hasWord(t, 'tide') ||
    hasWord(t, 'ripple')
  ) {
    return { shape: 'wave' }
  }
  if (
    hasWord(t, 'vortex') ||
    hasWord(t, 'spiral') ||
    hasWord(t, 'whirlpool') ||
    hasWord(t, 'tornado') ||
    hasWord(t, 'swirl') ||
    hasWord(t, 'twist') ||
    hasWord(t, 'cyclone')
  ) {
    return { shape: 'vortex' }
  }
  if (
    hasWord(t, 'firework') ||
    hasWord(t, 'celebration') ||
    hasWord(t, 'party') ||
    hasWord(t, 'explosion') ||
    hasWord(t, 'festival') ||
    hasWord(t, 'firecracker') ||
    hasWord(t, 'sparkler')
  ) {
    return { shape: 'fireworks' }
  }
  if (
    hasWord(t, 'lightning') ||
    hasWord(t, 'thunder') ||
    hasWord(t, 'bolt') ||
    hasWord(t, 'electric') ||
    hasWord(t, 'flash') ||
    hasWord(t, 'zap') ||
    hasWord(t, 'strike')
  ) {
    return { shape: 'lightning' }
  }
  if (
    hasWord(t, 'flower') ||
    hasWord(t, 'bloom') ||
    hasWord(t, 'petal') ||
    hasWord(t, 'garden') ||
    hasWord(t, 'rose') ||
    hasWord(t, 'nature') ||
    hasWord(t, 'blossom')
  ) {
    return { shape: 'flower' }
  }

  // Agent Shapes — Handled by resolveAgentShape() — not keyword-dependent
  // These keyword fallbacks exist as a secondary safety net for text-only detection
  if (hasWord(t, 'discover') || hasWord(t, 'explore') || hasWord(t, 'investigate')) {
    return { shape: 'discovery' }
  }
  if (
    hasWord(t, 'score') ||
    hasWord(t, 'rating') ||
    hasWord(t, 'evaluate') ||
    hasWord(t, 'assess') ||
    hasWord(t, 'grade') ||
    hasWord(t, 'points')
  ) {
    return { shape: 'scoring' }
  }
  if (
    hasWord(t, 'workshop') ||
    hasWord(t, 'training') ||
    hasWord(t, 'session') ||
    hasWord(t, 'course') ||
    hasWord(t, 'learn') ||
    hasWord(t, 'teach')
  ) {
    return { shape: 'workshop' }
  }
  if (
    hasWord(t, 'consult') ||
    hasWord(t, 'advice') ||
    hasWord(t, 'guidance') ||
    hasWord(t, 'expert') ||
    hasWord(t, 'advisor') ||
    hasWord(t, 'counsel')
  ) {
    return { shape: 'consulting' }
  }
  if (
    hasWord(t, 'close') ||
    hasWord(t, 'deal') ||
    hasWord(t, 'urgent') ||
    hasWord(t, 'finalize') ||
    hasWord(t, 'complete') ||
    hasWord(t, 'seal')
  ) {
    return { shape: 'closer' }
  }
  if (
    hasWord(t, 'summarize') ||
    hasWord(t, 'recap') ||
    hasWord(t, 'overview') ||
    hasWord(t, 'brief') ||
    hasWord(t, 'synopsis')
  ) {
    return { shape: 'summary' }
  }
  if (
    hasWord(t, 'proposal') ||
    hasWord(t, 'offer') ||
    hasWord(t, 'quote') ||
    hasWord(t, 'bid') ||
    hasWord(t, 'suggest') ||
    hasWord(t, 'recommend')
  ) {
    return { shape: 'proposal' }
  }
  if (
    hasWord(t, 'admin') ||
    hasWord(t, 'manage') ||
    hasWord(t, 'control') ||
    hasWord(t, 'settings') ||
    hasWord(t, 'configure') ||
    hasWord(t, 'system')
  ) {
    return { shape: 'admin' }
  }
  if (
    hasWord(t, 'retarget') ||
    hasWord(t, 'remarket') ||
    hasWord(t, 'reengage') ||
    hasPhrase(t, 'follow-up') ||
    hasWord(t, 'reconnect') ||
    hasWord(t, 'nurture')
  ) {
    return { shape: 'retargeting' }
  }

  const highlightRegex = /(\*\*|["'])([A-Za-z0-9\s]{2,15})\1/
  const match = text.match(highlightRegex)

  if (match && match[2]) {
    const keyword = match[2].trim()
    const ignore = [
      'this',
      'that',
      'here',
      'note',
      'user',
      'model',
      'system',
      'text',
      'code',
      'image',
      'data',
      'file',
      'report',
    ]

    if (!ignore.includes(keyword.toLowerCase()) && keyword.split(' ').length <= 2) {
      return { shape: 'text', textContent: keyword.toUpperCase() }
    }
  }

  return null
}
